import { isAfter, isBefore, startOfDay } from "date-fns";
import { utcToZonedTime } from "date-fns-tz";
import { z } from "zod";
import { ExperimentSchema } from "../domain/experiment.entity";

/**
 * "Today" as a calendar day, in `hotelTimeZone` when available, otherwise the browser's local
 * "today". Mirrors the pattern already established by
 * `experiment/domain/metricRangeResolver.ts`'s `hotelLocalCalendarDate`: `utcToZonedTime` reads
 * back correctly via *local* `Date` getters (`startOfDay` here), because it round-trips the
 * calendar value through the JS `Date` object's local setters internally.
 *
 * Falling back to the browser's local "today" when `hotelTimeZone` is `undefined` (property
 * configuration still loading, access denied, or an unparseable time zone string — see
 * `useHotelTimeZone()`) is a deliberate, narrow best-effort default: it only matters for the
 * residual case where the hotel's real time zone is unavailable client-side, with the backend's
 * independent V9 check remaining the authority in that case (see proposal.md, Business Rules
 * item 2).
 */
function todayInTimeZone(hotelTimeZone: string | undefined): Date {
  const now = new Date();
  if (!hotelTimeZone) {
    return startOfDay(now);
  }
  return startOfDay(utcToZonedTime(now, hotelTimeZone));
}

/**
 * Creation-time-only date rules layered on top of the shared `ExperimentSchema` (US-001), which
 * deliberately excludes them since they only make sense when creating a new experiment (an
 * already-persisted, already-started experiment's `startedAt` is necessarily in the past).
 *
 * A factory rather than a static schema because the "not earlier than today" rule needs the
 * hotel's real IANA time zone (`useHotelTimeZone()`, a React hook not available inside a plain
 * Zod schema module) resolved by the caller and passed in.
 */
export function createExperimentFormSchema(hotelTimeZone: string | undefined) {
  return ExperimentSchema.superRefine((experiment, ctx) => {
    // A missing `startedAt` is already rejected by `ExperimentSchema`'s own `z.date()` field
    // (a native "invalid_type" issue, the same mechanism `ticker`'s required-ness relies on in
    // `ExperimentEdit.tsx`) - verified empirically: zod does not invoke this `.superRefine` at all
    // when a required field fails its own base-type check, so `experiment.startedAt` is
    // guaranteed to be a real `Date` by the time this callback runs. `startDateRequired` is
    // therefore surfaced by the component checking the native issue's `type`, not by an
    // additional custom issue here.
    if (isBefore(startOfDay(experiment.startedAt), todayInTimeZone(hotelTimeZone))) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["startedAt"],
        message: "startDatePast",
      });
    }

    if (
      experiment.estimatedEndDate != null &&
      !isAfter(experiment.estimatedEndDate, experiment.startedAt)
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["estimatedEndDate"],
        message: "endBeforeStart",
      });
    }
  });
}
